import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmt12 } from "../lib/time";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Save, Flame, Mail, Receipt, Upload, RefreshCw } from "lucide-react";

const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem("fuego_admin_token") || "");
  const [tab, setTab] = useState("bookings");

  if (!token) return <Login onLogin={(t) => { localStorage.setItem("fuego_admin_token", t); setToken(t); }} />;

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-2">Admin</div>
            <h1 className="font-display text-4xl font-black uppercase tracking-tight">Control Center</h1>
          </div>
          <button
            onClick={() => { localStorage.removeItem("fuego_admin_token"); setToken(""); }}
            data-testid="admin-logout"
            className="border border-[#262626] hover:border-[#D90429] px-4 py-2 inline-flex items-center gap-2 font-display uppercase tracking-wider text-sm transition-colors"
          >
            <LogOut size={14}/> Logout
          </button>
        </div>

        <div className="flex gap-2 border-b border-[#262626] mb-8 overflow-x-auto">
          {["bookings", "schedule", "services", "gallery", "socials", "integrations"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`px-5 py-3 font-display uppercase tracking-wider text-sm transition-colors whitespace-nowrap ${
                tab === t ? "text-[#D90429] border-b-2 border-[#D90429] -mb-px" : "text-[#A3A3A3] hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "bookings" && <BookingsTab />}
        {tab === "schedule" && <ScheduleTab />}
        {tab === "services" && <ServicesTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "socials" && <SocialsTab />}
        {tab === "integrations" && <IntegrationsTab />}
      </div>
    </div>
  );
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await api.post("/admin/login", { email, password: pw });
      onLogin(r.data.token);
      toast.success("Logged in");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-[80vh] flex items-center" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md mx-auto px-6 py-16 border border-[#262626] bg-[#0a0a0a]">
        <Flame size={32} className="text-[#D90429] mb-6" />
        <h1 className="font-display text-3xl font-black uppercase tracking-tight">Admin Login</h1>
        <p className="mt-2 text-sm text-[#A3A3A3]">Restricted access — staff only.</p>
        <div className="mt-8 space-y-4">
          <input data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" className="w-full h-12 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-4 text-white"/>
          <input data-testid="admin-password" value={pw} onChange={(e) => setPw(e.target.value)} type="password" placeholder="Password" className="w-full h-12 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-4 text-white"/>
        </div>
        {err && (
          <div data-testid="admin-login-error" className="mt-4 px-4 py-3 border border-[#EF4444] bg-[#EF4444]/10 text-sm text-[#EF4444]">
            {err}
          </div>
        )}
        <button disabled={busy} type="submit" data-testid="admin-login-btn" className="mt-6 w-full bg-[#D90429] hover:bg-[#EF233C] disabled:bg-[#262626] text-white font-display font-bold tracking-wider uppercase px-6 py-4 transition-colors">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const BookingsTab = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/bookings").then(r => setItems(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);
  const update = async (id, status) => {
    try {
      const r = await api.patch(`/admin/bookings/${id}`, { status });
      if (r.data?.auto_invoice?.ok) {
        toast.success("Status updated · Balance invoice auto-sent to customer");
      } else if (r.data?.auto_invoice?.already_invoiced) {
        toast.success("Status updated · Balance was already invoiced");
      } else if (r.data?.review_email_sent) {
        toast.success("Status updated · Review request email sent to customer");
      } else {
        toast.success("Updated");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };
  const remove = async (id) => { if (!window.confirm("Delete booking?")) return; await api.delete(`/admin/bookings/${id}`); toast.success("Deleted"); load(); };
  const resendConfirmation = async (id) => {
    try { await api.post(`/admin/bookings/${id}/resend-confirmation`); toast.success("Confirmation email sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };
  const sendBalanceInvoice = async (id) => {
    if (!window.confirm("Email customer the balance invoice now?")) return;
    try { await api.post(`/admin/bookings/${id}/send-balance-invoice`); toast.success("Balance invoice emailed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to send invoice"); }
  };
  const syncJobber = async (id) => {
    const t = toast.loading("Syncing to Jobber…");
    try {
      const r = await api.post(`/admin/jobber/resync/${id}`);
      toast.dismiss(t);
      if (r.data?.ok) {
        toast.success("Synced to Jobber ✓");
      } else {
        toast.error(r.data?.message || "Jobber sync failed");
      }
      load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || e?.response?.data?.message || "Jobber sync failed");
    }
  };

  return (
    <div className="border border-[#262626] overflow-x-auto" data-testid="admin-bookings">
      <table className="w-full text-sm">
        <thead className="bg-[#0a0a0a] text-[#A3A3A3] uppercase tracking-wider text-xs">
          <tr>
            <th className="text-left p-4">Customer</th>
            <th className="text-left p-4">Service</th>
            <th className="text-left p-4">When</th>
            <th className="text-left p-4">Payment</th>
            <th className="text-left p-4">Status</th>
            <th className="text-left p-4">Jobber</th>
            <th className="p-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => {
            const balance = b.balance_amount ?? 0;
            const balanceStatus = b.balance_status || "n_a";
            return (
              <tr key={b.id} className="border-t border-[#1a1a1a] hover:bg-[#0a0a0a]">
                <td className="p-4">
                  <div className="font-semibold">{b.name}</div>
                  <div className="text-xs text-[#A3A3A3]">{b.email} · {b.phone}</div>
                  <div className="text-xs text-[#A3A3A3]">{b.vehicle_make_model}</div>
                  {b.service_address && <div className="text-xs text-[#D90429] mt-1">📍 {b.service_address}</div>}
                </td>
                <td className="p-4">{b.service_name}{b.vehicle_type ? ` (${b.vehicle_type})` : ""}</td>
                <td className="p-4">{b.date} · {fmt12(b.time)}</td>
                <td className="p-4">
                  <div>Total: <span className="text-white font-semibold">${b.amount.toFixed(2)}</span></div>
                  <div className="text-xs text-[#A3A3A3]">
                    Deposit: ${(b.deposit_amount ?? b.amount).toFixed(2)} ({b.payment_status})
                  </div>
                  {balance > 0 && (
                    <div className={`text-xs ${balanceStatus === "paid" ? "text-[#10B981]" : "text-[#D90429]"}`}>
                      Balance: ${balance.toFixed(2)} ({balanceStatus.replace("_", " ")})
                    </div>
                  )}
                </td>
                <td className="p-4">
                  <select value={b.status} onChange={(e) => update(b.id, e.target.value)} className="bg-[#050505] border border-[#262626] px-2 py-1" data-testid={`booking-status-${b.id}`}>
                    {["pending", "confirmed", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-4 text-xs">
                  {b.jobber?.jobber_job_id ? (
                    <span className="inline-flex items-center gap-1 border border-[#22c55e] text-[#22c55e] px-2 py-0.5" data-testid={`jobber-status-${b.id}`} title={`Synced ${b.jobber.last_synced_at || ""}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e]"/>Synced
                    </span>
                  ) : b.jobber?.status === "note_synced" ? (
                    <span className="inline-flex items-center gap-1 border border-[#22c55e] text-[#22c55e] px-2 py-0.5" data-testid={`jobber-status-${b.id}`} title="Client + address + note synced. Create Job manually in Jobber.">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e]"/>Note synced
                    </span>
                  ) : b.jobber?.status === "client_only" ? (
                    <span className="inline-flex items-center gap-1 border border-[#F59E0B] text-[#F59E0B] px-2 py-0.5" data-testid={`jobber-status-${b.id}`} title={b.jobber.last_error || "Client created but note/job failed"}>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]"/>Client only
                    </span>
                  ) : b.jobber?.status === "cancelled" ? (
                    <span className="inline-flex items-center gap-1 border border-[#A3A3A3] text-[#A3A3A3] px-2 py-0.5" data-testid={`jobber-status-${b.id}`}>Cancelled</span>
                  ) : (
                    <span className="text-[#A3A3A3]" data-testid={`jobber-status-${b.id}`}>—</span>
                  )}
                  {b.jobber?.last_error && !b.jobber?.jobber_job_id && !b.jobber?.note_synced && (
                    <div className="mt-1 text-[10px] text-[#EF4444] max-w-[220px] break-words">{b.jobber.last_error}</div>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => syncJobber(b.id)} data-testid={`booking-jobber-sync-${b.id}`} title="Sync to Jobber" className="text-[#A3A3A3] hover:text-[#D90429] p-2 transition-colors"><RefreshCw size={16}/></button>
                    <button onClick={() => resendConfirmation(b.id)} data-testid={`booking-resend-${b.id}`} title="Resend confirmation email" className="text-[#A3A3A3] hover:text-[#D90429] p-2 transition-colors"><Mail size={16}/></button>
                    {balance > 0 && balanceStatus !== "paid" && (
                      <button onClick={() => sendBalanceInvoice(b.id)} data-testid={`booking-invoice-${b.id}`} title="Send balance invoice" className="text-[#D90429] hover:text-[#EF233C] p-2 transition-colors"><Receipt size={16}/></button>
                    )}
                    <button onClick={() => remove(b.id)} data-testid={`booking-delete-${b.id}`} title="Delete booking" className="text-[#EF4444] hover:text-[#D90429] p-2 transition-colors"><Trash2 size={16}/></button>
                  </div>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr><td colSpan={7} className="p-8 text-center text-[#A3A3A3]">No bookings yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const ServicesTab = () => {
  const [services, setServices] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newSvc, setNewSvc] = useState({ name: "", short_name: "", description: "", price: "0", duration_minutes: "150", has_vehicle_types: false, vehicle_types: "" });
  const load = () => api.get("/services").then(r => setServices(r.data));
  useEffect(() => { load(); }, []);

  const parseVtList = (str) => str.split("\n").map(l => l.trim()).filter(Boolean).map(line => {
    const [type, price] = line.split(":").map(s => s.trim());
    return { type, price: parseFloat(price) || 0 };
  });

  const save = async (s) => {
    await api.patch(`/admin/services/${s.id}`, {
      name: s.name, short_name: s.short_name, description: s.description, price: parseFloat(s.price),
      duration_minutes: parseInt(s.duration_minutes, 10) || 150,
      has_vehicle_types: !!s.has_vehicle_types,
      vehicle_types: s.vehicle_types?.map(vt => ({ type: vt.type, price: parseFloat(vt.price) })),
      order: s.order,
    });
    toast.success("Service updated");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service permanently?")) return;
    try { await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const create = async () => {
    if (!newSvc.name) return toast.error("Name required");
    const vt = newSvc.has_vehicle_types ? parseVtList(newSvc.vehicle_types) : [];
    try {
      await api.post("/admin/services", {
        name: newSvc.name,
        short_name: newSvc.short_name || newSvc.name,
        description: newSvc.description,
        price: parseFloat(newSvc.price) || 0,
        duration_minutes: parseInt(newSvc.duration_minutes, 10) || 150,
        has_vehicle_types: !!newSvc.has_vehicle_types,
        vehicle_types: vt,
        order: services.length + 1,
      });
      setShowNew(false);
      setNewSvc({ name: "", short_name: "", description: "", price: "0", duration_minutes: "150", has_vehicle_types: false, vehicle_types: "" });
      toast.success("Service created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-services">
      <div className="flex justify-between items-center">
        <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429]">{services.length} active services</div>
        <button onClick={() => setShowNew(!showNew)} data-testid="svc-new-toggle" className="bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-5 py-3 inline-flex items-center gap-2 transition-colors">
          <Plus size={14}/> {showNew ? "Close" : "Add New Service"}
        </button>
      </div>

      {showNew && (
        <div className="border-2 border-dashed border-[#D90429]/60 bg-[#0a0a0a] p-6" data-testid="svc-new-form">
          <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-4">New Service</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" value={newSvc.name} onChange={(v) => setNewSvc({ ...newSvc, name: v })} testid="svc-new-name" />
            <Field label="Short name" value={newSvc.short_name} onChange={(v) => setNewSvc({ ...newSvc, short_name: v })} testid="svc-new-short" />
            <Field label="Base price ($)" type="number" value={newSvc.price} onChange={(v) => setNewSvc({ ...newSvc, price: v })} testid="svc-new-price" />
            <Field label="Duration (minutes)" type="number" value={newSvc.duration_minutes} onChange={(v) => setNewSvc({ ...newSvc, duration_minutes: v })} testid="svc-new-duration" />
            <div>
              <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#D90429]">Vehicle Type Dropdown?</label>
              <select value={newSvc.has_vehicle_types ? "yes" : "no"} onChange={(e) => setNewSvc({ ...newSvc, has_vehicle_types: e.target.value === "yes" })} className="mt-2 w-full h-11 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white" data-testid="svc-new-hvt">
                <option value="no">No — fixed price</option>
                <option value="yes">Yes — different price per vehicle type</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#D90429]">Description</label>
            <textarea value={newSvc.description} onChange={(e) => setNewSvc({ ...newSvc, description: e.target.value })} rows={2} className="mt-2 w-full bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-4 py-3 text-white" data-testid="svc-new-desc"/>
          </div>
          {newSvc.has_vehicle_types && (
            <div className="mt-4">
              <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#D90429]">Vehicle Types & Prices</label>
              <textarea
                value={newSvc.vehicle_types}
                onChange={(e) => setNewSvc({ ...newSvc, vehicle_types: e.target.value })}
                rows={4}
                placeholder={"One per line, format: Type: Price\nCar: 65\nSUV: 80\nTruck: 100\nMotorcycle: 80"}
                className="mt-2 w-full bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-4 py-3 text-white font-mono text-sm"
                data-testid="svc-new-vt"
              />
            </div>
          )}
          <button onClick={create} data-testid="svc-create-btn" className="mt-6 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-6 py-3 inline-flex items-center gap-2 transition-colors">
            <Plus size={14}/> Create Service
          </button>
        </div>
      )}

      {services.map((s, idx) => (
        <div key={s.id} className="border border-[#262626] bg-[#0a0a0a] p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="font-display uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3]">ID · {s.id}</div>
            <button onClick={() => remove(s.id)} data-testid={`svc-delete-${s.id}`} className="text-[#EF4444] hover:text-[#D90429] p-1 transition-colors"><Trash2 size={16}/></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" value={s.name} onChange={(v) => { const c = [...services]; c[idx].name = v; setServices(c); }} testid={`svc-name-${s.id}`} />
            <Field label="Short name" value={s.short_name} onChange={(v) => { const c = [...services]; c[idx].short_name = v; setServices(c); }} testid={`svc-short-${s.id}`} />
            <Field label="Base price" type="number" value={s.price} onChange={(v) => { const c = [...services]; c[idx].price = v; setServices(c); }} testid={`svc-price-${s.id}`} />
            <Field label="Duration (minutes)" type="number" value={s.duration_minutes ?? 150} onChange={(v) => { const c = [...services]; c[idx].duration_minutes = v; setServices(c); }} testid={`svc-duration-${s.id}`} />
            <div>
              <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#D90429]">Vehicle Type Dropdown?</label>
              <select value={s.has_vehicle_types ? "yes" : "no"} onChange={(e) => { const c = [...services]; c[idx].has_vehicle_types = e.target.value === "yes"; if (c[idx].has_vehicle_types && (!c[idx].vehicle_types || !c[idx].vehicle_types.length)) c[idx].vehicle_types = [{type:"Car", price: s.price}]; setServices(c); }} className="mt-2 w-full h-11 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white" data-testid={`svc-hvt-${s.id}`}>
                <option value="no">No — fixed price</option>
                <option value="yes">Yes — per vehicle</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#D90429]">Description</label>
            <textarea value={s.description} onChange={(e) => { const c = [...services]; c[idx].description = e.target.value; setServices(c); }} rows={2} className="mt-2 w-full bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-4 py-3 text-white" data-testid={`svc-desc-${s.id}`}/>
          </div>
          {s.has_vehicle_types && (
            <div className="mt-4">
              <div className="flex justify-between items-end mb-2">
                <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#D90429]">Vehicle Pricing</label>
                <button onClick={() => { const c = [...services]; c[idx].vehicle_types = [...(c[idx].vehicle_types || []), { type: "", price: 0 }]; setServices(c); }} data-testid={`svc-add-vt-${s.id}`} className="text-xs text-[#D90429] hover:text-[#EF233C] font-display uppercase tracking-wider">+ Add Vehicle Type</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(s.vehicle_types || []).map((vt, vi) => (
                  <div key={vi} className="border border-[#262626] p-3 relative">
                    <input value={vt.type} onChange={(e) => { const c = [...services]; c[idx].vehicle_types[vi].type = e.target.value; setServices(c); }} className="w-full bg-[#050505] border border-[#262626] px-2 py-1 text-xs" placeholder="Type" data-testid={`svc-vt-type-${s.id}-${vi}`}/>
                    <input type="number" value={vt.price} onChange={(e) => { const c = [...services]; c[idx].vehicle_types[vi].price = e.target.value; setServices(c); }} className="w-full bg-[#050505] border border-[#262626] px-2 py-1 mt-1 text-xs" placeholder="Price" data-testid={`svc-vt-price-${s.id}-${vi}`}/>
                    <button onClick={() => { const c = [...services]; c[idx].vehicle_types.splice(vi, 1); setServices(c); }} className="absolute top-1 right-1 text-[#EF4444] hover:text-[#D90429]"><Trash2 size={10}/></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button onClick={() => save(s)} data-testid={`svc-save-${s.id}`} className="mt-6 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-6 py-3 inline-flex items-center gap-2 transition-colors">
            <Save size={14}/> Save
          </button>
        </div>
      ))}
    </div>
  );
};

const GalleryTab = () => {
  const [items, setItems] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [servicePerformed, setServicePerformed] = useState("");
  const [resultSummary, setResultSummary] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editing, setEditing] = useState({});
  const [newT, setNewT] = useState({ name: "", vehicle: "", quote: "", rating: 5, is_active: true });
  const fileInputRef = React.useRef(null);

  const load = () => {
    api.get("/gallery").then(r => setItems(r.data));
    api.get("/admin/testimonials").then(r => setTestimonials(r.data));
    api.get("/gallery/config").then(r => setConfig(r.data));
    api.get("/gallery/categories").then(r => setCategories(r.data.categories));
  };
  useEffect(() => { load(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") && !file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|heic|heif)$/)) {
      return toast.error("Please pick an image file");
    }
    if (file.size > 15 * 1024 * 1024) return toast.error("Image must be under 15 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      await api.post("/admin/gallery", {
        url, caption, category, vehicle,
        service_performed: servicePerformed,
        result_summary: resultSummary,
      });
      setCaption(""); setCategory(""); setVehicle(""); setServicePerformed(""); setResultSummary("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Photo added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this photo?")) return; await api.delete(`/admin/gallery/${id}`); toast.success("Removed"); load(); };
  const startEdit = (it) => { setEditingId(it.id); setEditing({ ...it }); };
  const saveEdit = async () => {
    try {
      await api.patch(`/admin/gallery/${editingId}`, {
        caption: editing.caption, category: editing.category, vehicle: editing.vehicle,
        service_performed: editing.service_performed, result_summary: editing.result_summary,
        order: parseInt(editing.order, 10),
      });
      toast.success("Saved"); setEditingId(null); load();
    } catch { toast.error("Save failed"); }
  };
  const saveConfig = async (patch) => {
    const next = { ...config, ...patch };
    setConfig(next);
    try { await api.put("/admin/gallery/config", patch); } catch { toast.error("Save failed"); }
  };
  const toggleSection = (key) => saveConfig({ sections: { ...(config?.sections || {}), [key]: !(config?.sections?.[key] ?? true) } });
  const addTestimonial = async () => {
    if (!newT.name || !newT.quote) return toast.error("Name + quote required");
    try { await api.post("/admin/testimonials", newT); toast.success("Testimonial added"); setNewT({ name: "", vehicle: "", quote: "", rating: 5, is_active: true }); load(); }
    catch { toast.error("Failed"); }
  };
  const updateT = async (id, patch) => { await api.patch(`/admin/testimonials/${id}`, patch); load(); };
  const removeT = async (id) => { if (!window.confirm("Delete this testimonial?")) return; await api.delete(`/admin/testimonials/${id}`); load(); };

  if (!config) return <div className="text-[#A3A3A3]">Loading…</div>;
  const sec = config.sections || {};

  return (
    <div data-testid="admin-gallery" className="space-y-8">
      {/* Hero + CTA config */}
      <div className="border border-[#262626] bg-[#0a0a0a] p-6 space-y-4">
        <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429]">Hero & Sitewide CTA</div>
        <Field label="Hero headline" value={config.hero_headline || ""} onChange={(v) => saveConfig({ hero_headline: v })} testid="gallery-hero-headline"/>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Hero subtitle</label>
          <textarea rows={2} value={config.hero_subtitle || ""} onChange={(e) => saveConfig({ hero_subtitle: e.target.value })} data-testid="gallery-hero-subtitle" className="mt-1 w-full bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 py-2 text-white text-sm"/>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Button text" value={config.cta_text || ""} onChange={(v) => saveConfig({ cta_text: v })} testid="gallery-cta-text"/>
          <Field label="Button link (Jobber URL)" value={config.cta_link || ""} onChange={(v) => saveConfig({ cta_link: v })} testid="gallery-cta-link"/>
          <Field label="Repeat CTA every N photos" type="number" value={String(config.cta_repeat_after ?? 6)} onChange={(v) => saveConfig({ cta_repeat_after: parseInt(v, 10) || 6 })} testid="gallery-cta-repeat"/>
          <Field label="Phone number (for mobile Call Now)" value={config.phone_number || ""} onChange={(v) => saveConfig({ phone_number: v })} testid="gallery-phone"/>
        </div>
        <div className="flex flex-wrap gap-2 pt-2 border-t border-[#262626]">
          <span className="text-xs uppercase tracking-widest text-[#A3A3A3] mr-2 self-center">Sections:</span>
          {[["hero","Hero"],["testimonials","Testimonials"],["sticky_bar","Mobile Sticky Bar"]].map(([k, label]) => (
            <button key={k} onClick={() => toggleSection(k)} data-testid={`toggle-section-${k}`}
              className={`px-3 py-1 text-xs uppercase tracking-wider border transition-colors ${
                (sec[k] ?? true) ? "border-[#22c55e] text-[#22c55e]" : "border-[#EF4444] text-[#EF4444]"
              }`}
            >
              {(sec[k] ?? true) ? "ON" : "OFF"} · {label}
            </button>
          ))}
        </div>
      </div>

      {/* Upload */}
      <div className="border-2 border-dashed border-[#262626] hover:border-[#D90429]/60 bg-[#0a0a0a] p-6 transition-colors">
        <div className="flex items-center gap-2 mb-4">
          <Upload size={18} className="text-[#D90429]" />
          <div className="font-display uppercase tracking-wider text-sm font-bold">Add Gallery Photo</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (e.g. '2020 Honda Civic')" data-testid="gallery-new-caption" className="h-10 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white text-sm"/>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="gallery-new-category" className="h-10 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white text-sm">
            <option value="">— Pick category —</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="text" value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="Vehicle (e.g. '2020 Honda Civic - Black')" data-testid="gallery-new-vehicle" className="h-10 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white text-sm"/>
          <input type="text" value={servicePerformed} onChange={(e) => setServicePerformed(e.target.value)} placeholder="Service performed" data-testid="gallery-new-service" className="h-10 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white text-sm"/>
          <input type="text" value={resultSummary} onChange={(e) => setResultSummary(e.target.value)} placeholder="Result (one-liner)" data-testid="gallery-new-result" className="sm:col-span-2 h-10 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white text-sm"/>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="gallery-file-input" className="hidden"/>
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="gallery-upload-btn" className="bg-[#D90429] hover:bg-[#EF233C] disabled:bg-[#262626] text-white font-display font-bold tracking-wider uppercase px-6 py-2 inline-flex items-center gap-2 transition-colors text-sm">
            {uploading ? <><Flame size={14} className="animate-pulse"/> Uploading…</> : <><Upload size={14}/> Choose Photo</>}
          </button>
          <span className="text-xs text-[#A3A3A3]">Max 15 MB · JPG/PNG/HEIC/WebP</span>
        </div>
      </div>

      {/* Existing items */}
      <div>
        <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-3">Existing Photos ({items.length})</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <div key={it.id} className="relative border border-[#262626] bg-black" data-testid={`admin-gallery-item-${it.id}`}>
              <img src={it.url} alt={it.caption} className="w-full aspect-[4/3] object-cover"/>
              <div className="p-3 space-y-1 text-xs">
                {editingId === it.id ? (
                  <>
                    <input value={editing.caption || ""} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} placeholder="Caption" className="w-full h-8 bg-[#050505] border border-[#262626] px-2 text-white"/>
                    <select value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full h-8 bg-[#050505] border border-[#262626] px-2 text-white">
                      <option value="">— Category —</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input value={editing.vehicle || ""} onChange={(e) => setEditing({ ...editing, vehicle: e.target.value })} placeholder="Vehicle" className="w-full h-8 bg-[#050505] border border-[#262626] px-2 text-white"/>
                    <input value={editing.service_performed || ""} onChange={(e) => setEditing({ ...editing, service_performed: e.target.value })} placeholder="Service" className="w-full h-8 bg-[#050505] border border-[#262626] px-2 text-white"/>
                    <input value={editing.result_summary || ""} onChange={(e) => setEditing({ ...editing, result_summary: e.target.value })} placeholder="Result" className="w-full h-8 bg-[#050505] border border-[#262626] px-2 text-white"/>
                    <input type="number" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: e.target.value })} placeholder="Order" className="w-full h-8 bg-[#050505] border border-[#262626] px-2 text-white"/>
                    <div className="flex gap-2 pt-1">
                      <button onClick={saveEdit} data-testid={`gallery-save-${it.id}`} className="flex-1 bg-[#D90429] text-white px-2 py-1 uppercase tracking-wider text-xs">Save</button>
                      <button onClick={() => setEditingId(null)} className="flex-1 border border-[#262626] px-2 py-1 uppercase tracking-wider text-xs text-[#A3A3A3]">Cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    {it.category && <div className="text-[#D90429] uppercase tracking-wider">{it.category}</div>}
                    <div className="text-white font-bold">{it.caption || it.vehicle || "Untitled"}</div>
                    {it.service_performed && <div className="text-[#A3A3A3]">{it.service_performed}</div>}
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => startEdit(it)} data-testid={`gallery-edit-${it.id}`} className="flex-1 border border-[#262626] hover:border-[#D90429] px-2 py-1 uppercase tracking-wider text-[10px] text-white">Edit</button>
                      <button onClick={() => remove(it.id)} data-testid={`gallery-delete-${it.id}`} className="border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white px-2 py-1"><Trash2 size={12}/></button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <div className="border border-[#262626] bg-[#0a0a0a] p-6">
        <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-4">Testimonials</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={newT.name} onChange={(e) => setNewT({ ...newT, name: e.target.value })} placeholder="Customer name" data-testid="new-testimonial-name" className="h-10 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white text-sm"/>
          <input value={newT.vehicle} onChange={(e) => setNewT({ ...newT, vehicle: e.target.value })} placeholder="Vehicle (optional)" data-testid="new-testimonial-vehicle" className="h-10 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white text-sm"/>
        </div>
        <textarea rows={2} value={newT.quote} onChange={(e) => setNewT({ ...newT, quote: e.target.value })} placeholder="Quote" data-testid="new-testimonial-quote" className="mt-3 w-full bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 py-2 text-white text-sm"/>
        <div className="mt-3 flex items-center gap-3">
          <select value={newT.rating} onChange={(e) => setNewT({ ...newT, rating: parseInt(e.target.value, 10) })} className="h-10 bg-[#050505] border border-[#262626] px-3 text-white text-sm">
            {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
          </select>
          <button onClick={addTestimonial} data-testid="add-testimonial-btn" className="bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold uppercase tracking-wider px-5 py-2 text-sm">+ Add</button>
        </div>
        <div className="mt-6 space-y-2">
          {testimonials.map((t) => (
            <div key={t.id} className="border border-[#262626] p-3 flex items-start justify-between gap-4" data-testid={`admin-testimonial-${t.id}`}>
              <div className="flex-1 text-sm">
                <div className="text-white font-bold">{t.name} {t.vehicle && <span className="text-[#A3A3A3]">· {t.vehicle}</span>}</div>
                <div className="text-[#D4D4D4] italic mt-1">"{t.quote}"</div>
                <div className="text-[#A3A3A3] text-xs mt-1">{t.rating}★ · order {t.order} · {t.is_active ? "active" : "hidden"}</div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => updateT(t.id, { is_active: !t.is_active })} className="border border-[#262626] hover:border-[#D90429] px-2 py-1 text-[10px] uppercase text-white">{t.is_active ? "Hide" : "Show"}</button>
                <button onClick={() => removeT(t.id)} className="border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white px-2 py-1"><Trash2 size={12}/></button>
              </div>
            </div>
          ))}
          {testimonials.length === 0 && <div className="text-xs text-[#A3A3A3]">No testimonials yet — add one above.</div>}
        </div>
      </div>
    </div>
  );
};

const ScheduleTab = () => {
  const [blocks, setBlocks] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [hours, setHours] = useState(null);
  const [savingHours, setSavingHours] = useState(false);
  const load = () => api.get("/admin/blocks").then(r => setBlocks(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); api.get("/business-hours").then(r => setHours(r.data)); }, []);
  const add = async () => {
    if (!start || !end) return toast.error("Pick both dates");
    if (end < start) return toast.error("End date must be after start date");
    try {
      await api.post("/admin/blocks", { start_date: start, end_date: end, reason });
      setStart(""); setEnd(""); setReason(""); toast.success("Time blocked"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this block?")) return;
    await api.delete(`/admin/blocks/${id}`); toast.success("Removed"); load();
  };
  const updateHourDay = (idx, patch) => {
    setHours(h => ({ ...h, days: h.days.map((d, i) => i === idx ? { ...d, ...patch } : d) }));
  };
  const saveHours = async () => {
    setSavingHours(true);
    try {
      await api.put("/admin/business-hours", { days: hours.days });
      toast.success("Business hours saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingHours(false); }
  };
  return (
    <div data-testid="admin-schedule" className="space-y-6">
      <div className="border border-[#262626] bg-[#0a0a0a] p-6" data-testid="business-hours-card">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429]">Weekly Business Hours</div>
          <button
            onClick={saveHours}
            disabled={!hours || savingHours}
            data-testid="business-hours-save-btn"
            className="bg-[#D90429] hover:bg-[#EF233C] disabled:bg-[#262626] text-white font-display font-bold tracking-wider uppercase px-5 py-2 text-xs transition-colors"
          >
            {savingHours ? "Saving…" : "Save hours"}
          </button>
        </div>
        {!hours ? (
          <div className="text-[#A3A3A3] text-sm">Loading…</div>
        ) : (
          <div className="space-y-2">
            {hours.days.map((d, i) => (
              <div key={d.day} className="grid grid-cols-12 gap-3 items-center" data-testid={`hours-day-${d.day}`}>
                <div className="col-span-3 sm:col-span-2 font-display uppercase tracking-wider text-sm">{d.name}</div>
                <label className="col-span-3 sm:col-span-2 flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={d.open}
                    onChange={(e) => updateHourDay(i, { open: e.target.checked })}
                    data-testid={`hours-day-${d.day}-open`}
                    className="w-4 h-4 accent-[#D90429]"
                  />
                  <span className={d.open ? "text-white" : "text-[#A3A3A3]"}>{d.open ? "Open" : "Closed"}</span>
                </label>
                <input
                  type="time"
                  value={d.start}
                  disabled={!d.open}
                  onChange={(e) => updateHourDay(i, { start: e.target.value })}
                  data-testid={`hours-day-${d.day}-start`}
                  className="col-span-3 sm:col-span-3 h-10 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white disabled:opacity-40"
                />
                <span className="col-span-1 text-center text-[#A3A3A3] text-xs">to</span>
                <input
                  type="time"
                  value={d.end}
                  disabled={!d.open}
                  onChange={(e) => updateHourDay(i, { end: e.target.value })}
                  data-testid={`hours-day-${d.day}-end`}
                  className="col-span-2 sm:col-span-3 h-10 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white disabled:opacity-40"
                />
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 text-xs text-[#A3A3A3]">Customers can only book hourly slots between Start and End on open days. End time is exclusive — e.g. 8:00 → 18:00 = last bookable slot is 5:00 PM.</p>
      </div>

      <div className="border border-[#262626] bg-[#0a0a0a] p-6">
        <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-4">Block Time Off</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3]">Start Date</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="block-start" className="mt-2 w-full h-11 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white"/>
          </div>
          <div>
            <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3]">End Date</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="block-end" className="mt-2 w-full h-11 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white"/>
          </div>
          <div>
            <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3]">Reason (optional)</label>
            <input type="text" placeholder="Vacation, Holiday…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="block-reason" className="mt-2 w-full h-11 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-3 text-white"/>
          </div>
          <div className="flex items-end">
            <button onClick={add} data-testid="block-add-btn" className="w-full h-11 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase inline-flex items-center justify-center gap-2 transition-colors">
              <Plus size={14}/> Block
            </button>
          </div>
        </div>
        <p className="mt-3 text-xs text-[#A3A3A3]">Customers won't be able to book any day between (and including) start and end. Use this for vacations, holidays, or equipment maintenance.</p>
      </div>

      <div className="border border-[#262626] bg-[#0a0a0a]">
        <div className="px-6 py-4 border-b border-[#262626] font-display uppercase tracking-[0.25em] text-xs text-[#D90429]">Blocked Periods</div>
        {blocks.length === 0 ? (
          <div className="p-8 text-center text-[#A3A3A3] text-sm">No time blocks set. Your calendar is fully open.</div>
        ) : (
          <ul className="divide-y divide-[#1a1a1a]">
            {blocks.map((b) => (
              <li key={b.id} className="px-6 py-4 flex items-center justify-between" data-testid={`block-${b.id}`}>
                <div>
                  <div className="font-display uppercase tracking-wider text-sm">
                    {b.start_date} {b.start_date !== b.end_date && `→ ${b.end_date}`}
                  </div>
                  {b.reason && <div className="text-xs text-[#A3A3A3] mt-1">{b.reason}</div>}
                </div>
                <button onClick={() => remove(b.id)} data-testid={`block-delete-${b.id}`} className="text-[#EF4444] hover:text-[#D90429] p-2 transition-colors"><Trash2 size={16}/></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const SocialsTab = () => {
  const [s, setS] = useState({ instagram: "", facebook: "", tiktok: "", google_business: "" });
  useEffect(() => { api.get("/socials").then(r => setS(r.data)); }, []);
  const save = async () => { await api.put("/admin/socials", s); toast.success("Socials saved"); };
  return (
    <div className="border border-[#262626] bg-[#0a0a0a] p-6 max-w-2xl space-y-4" data-testid="admin-socials">
      {["instagram", "facebook", "tiktok", "google_business"].map((k) => (
        <Field key={k} label={k.replace("_", " ")} value={s[k]} onChange={(v) => setS({ ...s, [k]: v })} testid={`social-${k}`} />
      ))}
      <button onClick={save} data-testid="socials-save" className="bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-6 py-3 inline-flex items-center gap-2"><Save size={14}/> Save</button>
    </div>
  );
};

const IntegrationsTab = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/jobber/status")
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ connected: false, configured: false }))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // Detect ?jobber_connected=1 or ?jobber_error from OAuth callback redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get("jobber_connected") === "1") {
      toast.success("Jobber connected successfully");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("jobber_error")) {
      toast.error(`Jobber connection failed: ${params.get("jobber_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = () => {
    const token = localStorage.getItem("fuego_admin_token");
    // Backend URL from same env var frontend already uses
    const base = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${base}/api/admin/jobber/auth/start?token=${encodeURIComponent(token)}`;
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Jobber? New bookings will stop syncing until you reconnect.")) return;
    try {
      await api.post("/admin/jobber/disconnect");
      toast.success("Jobber disconnected");
      load();
    } catch { toast.error("Failed"); }
  };

  const diagnose = async () => {
    const t = toast.loading("Introspecting Jobber schema…");
    try {
      const r = await api.get("/admin/jobber/diagnose");
      toast.dismiss(t);
      const txt = JSON.stringify(r.data, null, 2);
      try { await navigator.clipboard.writeText(txt); } catch { /* ignore */ }
      const w = window.open("", "_blank", "width=800,height=600");
      if (w) {
        w.document.body.style.fontFamily = "monospace";
        w.document.body.style.padding = "20px";
        w.document.body.style.whiteSpace = "pre-wrap";
        w.document.body.textContent = txt;
      }
      toast.success("Schema diagnostics ready — copied to clipboard + opened in new tab");
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || "Diagnose failed");
    }
  };

  const testSync = async () => {
    if (!window.confirm("Send a fake TEST BOOKING to your Jobber account?\n\nThis will create a new client named 'TEST BOOKING — Delete Me' in your real Jobber. You can delete it after from the mobile app.")) return;
    const t = toast.loading("Sending test to Jobber…");
    try {
      const r = await api.post("/admin/jobber/test-sync");
      toast.dismiss(t);
      const txt = JSON.stringify(r.data, null, 2);
      const w = window.open("", "_blank", "width=700,height=600");
      if (w) {
        w.document.body.style.fontFamily = "monospace";
        w.document.body.style.padding = "20px";
        w.document.body.style.whiteSpace = "pre-wrap";
        w.document.body.textContent = txt;
      }
      if (r.data?.ok) {
        toast.success("Test sent — check your Jobber mobile app!");
      } else {
        toast.error(r.data?.last_error || "Sync returned no client id");
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  const testCleanup = async () => {
    if (!window.confirm("Delete local TEST BOOKING records? (You still need to delete them from Jobber directly.)")) return;
    try {
      const r = await api.post("/admin/jobber/test-sync/cleanup");
      toast.success(`Removed ${r.data.removed_from_local_db} test booking(s) locally. Delete from Jobber via mobile app.`);
    } catch { toast.error("Cleanup failed"); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-integrations">
      <div className="border border-[#262626] bg-[#0a0a0a] p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429]">Field Service Sync</div>
            <h2 className="mt-1 font-display text-2xl font-black uppercase tracking-tight">Jobber</h2>
            <p className="mt-2 text-sm text-[#A3A3A3] max-w-lg">
              Auto-create Jobber clients + jobs the moment a customer pays their deposit. Reschedules,
              balance payments, and cancellations sync too.
            </p>
          </div>
          {loading ? (
            <div className="text-[#A3A3A3] text-sm">Loading…</div>
          ) : status?.connected ? (
            <div className="inline-flex items-center gap-2 border border-[#22c55e] text-[#22c55e] px-3 py-1 text-xs font-display uppercase tracking-wider" data-testid="jobber-status-badge">
              <span className="w-2 h-2 rounded-full bg-[#22c55e]"/>Connected
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 border border-[#EF4444] text-[#EF4444] px-3 py-1 text-xs font-display uppercase tracking-wider" data-testid="jobber-status-badge">
              <span className="w-2 h-2 rounded-full bg-[#EF4444]"/>Not connected
            </div>
          )}
        </div>

        {!loading && !status?.configured && (
          <div className="mt-6 border border-[#EF4444] bg-[#EF4444]/10 p-4 text-sm text-[#FCA5A5]">
            Jobber credentials aren&apos;t set on the server. Check that JOBBER_CLIENT_ID and JOBBER_CLIENT_SECRET are in the backend .env file.
          </div>
        )}

        {!loading && status?.configured && (
          <div className="mt-6 flex flex-wrap gap-3">
            {status.connected ? (
              <>
                <button
                  onClick={testSync}
                  data-testid="jobber-test-sync-btn"
                  className="bg-[#22c55e] hover:bg-[#16a34a] text-black font-display font-bold tracking-wider uppercase px-6 py-3 text-sm transition-colors"
                >
                  ▶ Send Test to Jobber
                </button>
                <button
                  onClick={diagnose}
                  data-testid="jobber-diagnose-btn"
                  className="border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black font-display uppercase tracking-wider px-5 py-3 text-sm transition-colors"
                >
                  Diagnose Schema
                </button>
                <button
                  onClick={testCleanup}
                  data-testid="jobber-test-cleanup-btn"
                  className="border border-[#262626] text-[#A3A3A3] hover:border-[#EF4444] hover:text-[#EF4444] font-display uppercase tracking-wider px-5 py-3 text-xs transition-colors"
                >
                  Delete Local Test
                </button>
                <button
                  onClick={connect}
                  data-testid="jobber-reconnect-btn"
                  className="border border-[#262626] hover:border-[#D90429] text-white font-display uppercase tracking-wider px-5 py-3 text-sm transition-colors"
                >
                  Reconnect
                </button>
                <button
                  onClick={disconnect}
                  data-testid="jobber-disconnect-btn"
                  className="border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white font-display uppercase tracking-wider px-5 py-3 text-sm transition-colors"
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={connect}
                data-testid="jobber-connect-btn"
                className="bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-6 py-3 text-sm transition-colors"
              >
                Connect Jobber
              </button>
            )}
          </div>
        )}

        {status?.connected && (
          <div className="mt-6 border-t border-[#262626] pt-4 text-xs text-[#A3A3A3] space-y-1">
            <div>Scopes: <span className="text-white">{status.scope || "—"}</span></div>
            <div>Token expires: <span className="text-white">{status.expires_at || "—"}</span></div>
            <div>Last updated: <span className="text-white">{status.updated_at || "—"}</span></div>
          </div>
        )}
      </div>

      <div className="border border-[#262626] bg-[#0a0a0a] p-6 text-sm text-[#A3A3A3] space-y-2">
        <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-2">How it works</div>
        <div>• <span className="text-white">Booking paid</span> → creates Jobber Client + Job with the appointment time, address & customer notes</div>
        <div>• <span className="text-white">Customer reschedules</span> → updates the Jobber Job schedule</div>
        <div>• <span className="text-white">Balance paid</span> → adds a payment note to the Jobber Job</div>
        <div>• <span className="text-white">Admin cancels</span> → adds a CANCELLED note to the Jobber Job</div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#D90429]">{label}</label>
    <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full h-11 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-4 text-white"/>
  </div>
);

export default AdminPage;
